import React, { useCallback, useEffect, useState } from "react";
import { Plus, Receipt, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { billing, fmtMoney, StatusPill, openAuthed } from "@/lib/billingApi";
import { useBT } from "@/i18n/billingText";
import { ProposalEditor } from "@/components/billing/ProposalEditor";
import { ProposalActions, InvoiceActions, PaymentVerifyActions } from "@/components/billing/BillingActions";
import { fileHref } from "@/components/account/accountConstants";

export const BillingPanel = ({ processId, destination, hasCustomer = true }) => {
  const { bt } = useBT();
  const [data, setData] = useState(null);
  const [editor, setEditor] = useState({ open: false, proposal: null });

  const load = useCallback(() => {
    billing.forCase(processId).then(setData).catch(() => setData({ proposals: [], invoices: [], payments: [] }));
  }, [processId]);
  useEffect(() => { load(); }, [load]);

  if (!data) return null;
  return (
    <section id="billing" className="rounded-2xl border border-slate-800 bg-slate-900/40 p-5 space-y-5" data-testid="case-billing">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-semibold flex items-center gap-2"><Receipt className="h-5 w-5 text-venka-green-light" />{bt("billing")}</h2>
        {hasCustomer && (
          <Button size="sm" className="bg-venka-green text-slate-950 hover:bg-venka-green-light" onClick={() => setEditor({ open: true, proposal: null })} data-testid="case-new-proposal">
            <Plus className="h-4 w-4 mr-1" />{bt("newProposal")}
          </Button>
        )}
      </div>

      <div>
        <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">{bt("proposals")}</h3>
        {data.proposals.length === 0 && <p className="text-sm text-slate-500">{bt("none")}</p>}
        <div className="space-y-2">
          {data.proposals.map((p) => (
            <div key={p.id} className="rounded-xl border border-slate-800 bg-slate-950/40 p-3" data-testid={`case-proposal-${p.status}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-medium">{p.number || p.title || "—"}</span>
                  <StatusPill status={p.status} label={bt(`st.${p.status}`)} />
                  {p.source === "ai" && <span className="inline-flex items-center gap-1 text-[11px] text-fuchsia-300"><Sparkles className="h-3 w-3" />{bt("aiDraft")}</span>}
                  <span className="text-slate-500 text-xs">{p.entity_code} · {(p.created_at || "").slice(0, 10)} · {p.created_by_name}</span>
                </div>
                <span className="font-semibold tabular-nums">{fmtMoney(p.total, p.currency)}</span>
              </div>
              <ProposalActions p={p} onChange={load} onEdit={(pp) => setEditor({ open: true, proposal: pp })} />
              {p.history?.length > 0 && (
                <details className="mt-2 text-xs text-slate-500">
                  <summary className="cursor-pointer">{bt("history")}</summary>
                  <ul className="mt-1 space-y-0.5">
                    {p.history.map((h, i) => <li key={i}>{(h.at || "").slice(0, 16).replace("T", " ")} · {h.by} · {h.action}{h.reason ? ` — ${h.reason}` : ""}</li>)}
                  </ul>
                </details>
              )}
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">{bt("invoices")}</h3>
        {data.invoices.length === 0 && <p className="text-sm text-slate-500">{bt("none")}</p>}
        <div className="space-y-2">
          {data.invoices.map((i) => (
            <div key={i.id} className="rounded-xl border border-slate-800 bg-slate-950/40 p-3" data-testid={`case-invoice-${i.kind}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-2 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{bt(`kind.${i.kind}`)} {i.number}</span>
                  <StatusPill status={i.status} label={bt(`st.${i.status}`)} />
                  {i.fiscal_invoice_number && <span className="text-xs text-slate-400">AGT: {i.fiscal_invoice_number}</span>}
                  {i.kind !== "credit_note" && <span className="text-xs text-slate-500">{bt("due")} {(i.due_at || "").slice(0, 10)}</span>}
                </div>
                <span className="tabular-nums">
                  {fmtMoney(i.totals?.total, i.currency)}
                  {i.kind !== "credit_note" && <span className="text-slate-400 text-xs ml-2">{bt("balance")}: {fmtMoney(i.balance, i.currency)}</span>}
                </span>
              </div>
              {i.kind !== "credit_note" && <InvoiceActions inv={i} onChange={load} />}
            </div>
          ))}
        </div>
      </div>

      {data.payments.length > 0 && (
        <div>
          <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">{bt("payments")}</h3>
          <div className="space-y-1.5">
            {data.payments.filter((p) => p.status !== "pending_card").map((p) => (
              <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-800 px-3 py-2 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="tabular-nums font-medium">{fmtMoney(p.amount, p.currency)}</span>
                  <span className="text-slate-400 text-xs">{bt(`methods.${p.method}`)} · {p.invoice_number} · {(p.created_at || "").slice(0, 10)}</span>
                  <StatusPill status={p.status} label={bt(`st.${p.status}`)} />
                  {p.proof_url && <a className="text-xs text-venka-green-light underline" href={fileHref(p.proof_url)} target="_blank" rel="noreferrer">{bt("proof")}</a>}
                  {p.status === "confirmed" && <button type="button" className="text-xs text-venka-green-light underline" onClick={() => openAuthed(`/billing/payments/${p.id}/receipt.pdf`)} data-testid="payment-receipt">{bt("receipt")}</button>}
                </div>
                <PaymentVerifyActions pay={p} onChange={load} />
              </div>
            ))}
          </div>
        </div>
      )}

      <ProposalEditor open={editor.open} onOpenChange={(o) => setEditor((e) => ({ ...e, open: o }))} processId={processId}
        destination={destination} suggestedEntity={data.suggested_entity} proposal={editor.proposal} onSaved={load} />
    </section>
  );
};

export default BillingPanel;
